// frontend/src/components/CampaignManager/useCampaignData.ts
import { useState } from 'react';
import { getApiUrl } from '../../config';
import {
  Lottery,
  Callcenter,
  Campaign,
  Action,
  PhoneNumber,
  Werbeweg,
  Werbetraeger,
  Vermittler,
  Versand
} from './types';

export const useCampaignData = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // State for all entities
  const [lotteries, setLotteries] = useState<Lottery[]>([]);
  const [callcenters, setCallcenters] = useState<Callcenter[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [actions, setActions] = useState<Action[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [werbewege, setWerbewege] = useState<Werbeweg[]>([]);
  const [werbetraeger, setWerbetraeger] = useState<Werbetraeger[]>([]);
  const [vermittler, setVermittler] = useState<Vermittler[]>([]);
  const [versand, setVersand] = useState<Versand[]>([]);

  // Generic fetch with error handling and authentication
  const fetchWithErrorHandling = async (url: string, options?: RequestInit) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {
        ...options?.headers,
      };
      if (token) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, { ...options, headers });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      return null;
    } catch (err: any) {
      if (err.message.includes('502')) {
        throw new Error('Backend-Server ist nicht erreichbar (502 Bad Gateway)');
      }
      throw err;
    }
  };

  // Load all data with combined endpoint (9 requests -> 1 request)
  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWithErrorHandling(getApiUrl('/campaign-manager/init'));
      if (data) {
        setLotteries(data.lotteries || []);
        setCallcenters(data.callcenters || []);
        setCampaigns(data.campaigns || []);
        setActions(data.actions || []);
        setPhoneNumbers(data.phoneNumbers || []);
        setWerbewege(data.werbewege || []);
        setWerbetraeger(data.werbetraeger || []);
        setVermittler(data.vermittler || []);
        setVersand(data.versand || []);
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  // Lotteries
  const loadLotteries = async () => {
    const data = await fetchWithErrorHandling(getApiUrl('/lotteries'));
    setLotteries(data || []);
  };

  const saveLottery = async (lottery: Partial<Lottery>, id?: number) => {
    const url = id ? getApiUrl(`/lotteries/${id}`) : getApiUrl('/lotteries');
    await fetchWithErrorHandling(url, {
      method: id ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(lottery)
    });
    await loadLotteries();
    setSuccessMessage('Lotterie erfolgreich gespeichert');
  };

  const deleteLottery = async (id: number) => {
    await fetchWithErrorHandling(getApiUrl(`/lotteries/${id}`), { method: 'DELETE' });
    await loadLotteries();
    setSuccessMessage('Lotterie erfolgreich gelöscht');
  };

  // Callcenters
  const loadCallcenters = async () => {
    const data = await fetchWithErrorHandling(getApiUrl('/callcenters'));
    setCallcenters(data || []);
  };

  const saveCallcenter = async (callcenter: Partial<Callcenter>, id?: number) => {
    const url = id ? getApiUrl(`/callcenters/${id}`) : getApiUrl('/callcenters');
    await fetchWithErrorHandling(url, {
      method: id ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(callcenter)
    });
    await loadCallcenters();
    setSuccessMessage('Callcenter erfolgreich gespeichert');
  };

  const deleteCallcenter = async (id: number) => {
    await fetchWithErrorHandling(getApiUrl(`/callcenters/${id}`), { method: 'DELETE' });
    await loadCallcenters();
    setSuccessMessage('Callcenter erfolgreich gelöscht');
  };

  // Campaigns
  const loadCampaigns = async () => {
    const data = await fetchWithErrorHandling(getApiUrl('/campaigns'));
    setCampaigns(data || []);
  };

  const saveCampaign = async (campaign: Partial<Campaign>, id?: number) => {
    const url = id ? getApiUrl(`/campaigns/${id}`) : getApiUrl('/campaigns');
    await fetchWithErrorHandling(url, {
      method: id ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(campaign)
    });
    await loadCampaigns();
    setSuccessMessage('Kampagne erfolgreich gespeichert');
  };

  const deleteCampaign = async (id: number) => {
    await fetchWithErrorHandling(getApiUrl(`/campaigns/${id}`), { method: 'DELETE' });
    await loadCampaigns();
    setSuccessMessage('Kampagne erfolgreich gelöscht');
  };

  const copyCampaign = async (id: number, targetLotteryId: number) => {
    await fetchWithErrorHandling(getApiUrl(`/campaigns/${id}/copy`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ target_lottery_id: targetLotteryId })
    });
    await loadCampaigns();
    setSuccessMessage('Kampagne erfolgreich kopiert');
  };

  // Actions
  const loadActions = async () => {
    const data = await fetchWithErrorHandling(getApiUrl('/actions'));
    setActions(data || []);
  };

  const saveAction = async (action: Partial<Action>, id?: number) => {
    const url = id ? getApiUrl(`/actions/${id}`) : getApiUrl('/actions');
    await fetchWithErrorHandling(url, {
      method: id ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(action)
    });
    await loadActions();
    setSuccessMessage('Aktion erfolgreich gespeichert');
  };

  const deleteAction = async (id: number) => {
    await fetchWithErrorHandling(getApiUrl(`/actions/${id}`), { method: 'DELETE' });
    await loadActions();
    setSuccessMessage('Aktion erfolgreich gelöscht');
  };

  const copyAction = async (id: number, targetLotteryId: number) => {
    await fetchWithErrorHandling(getApiUrl(`/actions/${id}/copy`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ target_lottery_id: targetLotteryId })
    });
    await loadActions();
    setSuccessMessage('Aktion erfolgreich kopiert');
  };

  const bulkCopyCampaigns = async (campaignIds: number[], targetLotteryId: number, copyActions?: boolean) => {
    const result = await fetchWithErrorHandling(getApiUrl('/campaigns/bulk-copy'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        campaign_ids: campaignIds,
        target_lottery_id: targetLotteryId,
        copy_actions: copyActions
      })
    });
    await Promise.all([loadCampaigns(), loadActions()]);
    setSuccessMessage(result?.message || `${campaignIds.length} Kampagne(n) erfolgreich kopiert`);
  };

  const bulkCopyActions = async (actionIds: number[], targetLotteryId: number) => {
    const result = await fetchWithErrorHandling(getApiUrl('/actions/bulk-copy'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action_ids: actionIds,
        target_lottery_id: targetLotteryId
      })
    });
    await loadActions();
    setSuccessMessage(result?.message || `${actionIds.length} Aktion(en) erfolgreich kopiert`);
  };

  // Phone Numbers
  const loadPhoneNumbers = async () => {
    const data = await fetchWithErrorHandling(getApiUrl('/phone-numbers'));
    setPhoneNumbers(data || []);
  };

  const savePhone = async (phone: Partial<PhoneNumber>, id?: number) => {
    const url = id ? getApiUrl(`/phone-numbers/${id}`) : getApiUrl('/phone-numbers');
    await fetchWithErrorHandling(url, {
      method: id ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(phone)
    });
    await loadPhoneNumbers();
    setSuccessMessage('Telefonnummer erfolgreich gespeichert');
  };

  const deletePhone = async (id: number) => {
    await fetchWithErrorHandling(getApiUrl(`/phone-numbers/${id}`), { method: 'DELETE' });
    await loadPhoneNumbers();
    setSuccessMessage('Telefonnummer erfolgreich gelöscht');
  };

  // Werbewege
  const loadWerbewege = async () => {
    const data = await fetchWithErrorHandling(getApiUrl('/werbewege'));
    setWerbewege(data || []);
  };

  const saveWerbeweg = async (werbeweg: Partial<Werbeweg>, id?: number) => {
    const url = id ? getApiUrl(`/werbewege/${id}`) : getApiUrl('/werbewege');
    await fetchWithErrorHandling(url, {
      method: id ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(werbeweg)
    });
    await loadWerbewege();
    setSuccessMessage('Werbeweg erfolgreich gespeichert');
  };

  const deleteWerbeweg = async (id: number) => {
    await fetchWithErrorHandling(getApiUrl(`/werbewege/${id}`), { method: 'DELETE' });
    await loadWerbewege();
    setSuccessMessage('Werbeweg erfolgreich gelöscht');
  };

  // Werbeträger
  const loadWerbetraeger = async () => {
    const data = await fetchWithErrorHandling(getApiUrl('/werbetraeger'));
    setWerbetraeger(data || []);
  };

  const saveWerbetraeger = async (werbetraeger: Partial<Werbetraeger>, id?: number) => {
    const url = id ? getApiUrl(`/werbetraeger/${id}`) : getApiUrl('/werbetraeger');
    await fetchWithErrorHandling(url, {
      method: id ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(werbetraeger)
    });
    await loadWerbetraeger();
    setSuccessMessage('Werbeträger erfolgreich gespeichert');
  };

  const deleteWerbetraeger = async (id: number) => {
    await fetchWithErrorHandling(getApiUrl(`/werbetraeger/${id}`), { method: 'DELETE' });
    await loadWerbetraeger();
    setSuccessMessage('Werbeträger erfolgreich gelöscht');
  };

  // Vermittler
  const loadVermittler = async () => {
    const data = await fetchWithErrorHandling(getApiUrl('/vermittler'));
    setVermittler(data || []);
  };

  const saveVermittler = async (vermittler: Partial<Vermittler>, id?: number) => {
    const url = id ? getApiUrl(`/vermittler/${id}`) : getApiUrl('/vermittler');
    await fetchWithErrorHandling(url, {
      method: id ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(vermittler)
    });
    await loadVermittler();
    setSuccessMessage('Vermittler erfolgreich gespeichert');
  };

  const deleteVermittler = async (id: number) => {
    await fetchWithErrorHandling(getApiUrl(`/vermittler/${id}`), { method: 'DELETE' });
    await loadVermittler();
    setSuccessMessage('Vermittler erfolgreich gelöscht');
  };

  // Versand
  const loadVersand = async () => {
    const data = await fetchWithErrorHandling(getApiUrl('/versand'));
    setVersand(data || []);
  };

  const saveVersand = async (versand: Partial<Versand>, id?: number) => {
    const url = id ? getApiUrl(`/versand/${id}`) : getApiUrl('/versand');
    await fetchWithErrorHandling(url, {
      method: id ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(versand)
    });
    await loadVersand();
    setSuccessMessage('Versand erfolgreich gespeichert');
  };

  const deleteVersand = async (id: number) => {
    await fetchWithErrorHandling(getApiUrl(`/versand/${id}`), { method: 'DELETE' });
    await loadVersand();
    setSuccessMessage('Versand erfolgreich gelöscht');
  };

  return {
    // State
    loading,
    error,
    successMessage,
    lotteries,
    callcenters,
    campaigns,
    actions,
    phoneNumbers,
    werbewege,
    werbetraeger,
    vermittler,
    versand,
    // Actions
    setError,
    setSuccessMessage,
    loadAll,
    // Lotteries
    saveLottery,
    deleteLottery,
    // Callcenters
    saveCallcenter,
    deleteCallcenter,
    // Campaigns
    saveCampaign,
    deleteCampaign,
    copyCampaign,
    bulkCopyCampaigns,
    // Actions
    saveAction,
    deleteAction,
    copyAction,
    bulkCopyActions,
    // Phone Numbers
    savePhone,
    deletePhone,
    // Werbewege
    loadWerbewege,
    saveWerbeweg,
    deleteWerbeweg,
    // Werbeträger
    loadWerbetraeger,
    saveWerbetraeger,
    deleteWerbetraeger,
    // Vermittler
    loadVermittler,
    saveVermittler,
    deleteVermittler,
    // Versand
    saveVersand,
    deleteVersand
  };
};
